import { Container, Button, Alert, Badge } from 'react-bootstrap';
import { ArrowRepeat } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import OffCanvasSidebar from 'components/base/OffCanvasSidebar';
import ToggleUseLocalTimezone from 'components/common/ToggleUseLocalTimezone';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import FilterAndSearchRefactor from 'components/forms/FilterAndSearchRefactor';
import { getErrorMessage } from 'helpers/getErrorMessage';
import { useMemo, useState } from 'react';
import { Splitter } from 'components/Splitter/Splitter';
import './TableViewLayout.scss';

// With a side panel open, the splitter sizes the TABLE and the panel takes the rest,
// so resizing the window grows or shrinks the panel rather than the list. Below the
// panel's minimum the table gives way instead, so the panel (and its close button)
// never disappears.
const SIDE_PANEL_MIN_WIDTH = 360;
const TABLE_MIN_WIDTH = 400;
const TABLE_MAX_WIDTH = 1400;
const TABLE_DEFAULT_WIDTH = 640;

// Count filter number
const countActiveFilters = (filters: Record<string, any>, filtersConfig: readonly any[]): number => {
  if (!filters || !filtersConfig?.length) return 0;

  const parentOnlyConfig = filtersConfig.find((config) => config.key === 'parentOnly');

  const baseCount = filtersConfig.reduce((count, config) => {
    const value = filters[config.key];
    const defaultVal = config.default;

    if (config.key === 'parentOnly') return count;

    // Check if filter has value
    switch (config.type) {
      case 'search':
      case 'dropdown':
        if (value && value !== '' && value !== defaultVal) return count + 1;
        break;
      case 'multi-dropdown':
        if (Array.isArray(value) && value.length > 0) return count + 1;
        break;
      case 'checkbox':
        if (value === true && defaultVal !== true) return count + 1;
        break;
      case 'date':
        if (value?.startDate || value?.endDate) return count + 1;
        break;
    }
    return count;
  }, 0);

  const parentOnlyValue = filters.parentOnly;
  if (parentOnlyValue !== true) return baseCount;
  if (parentOnlyConfig?.default === true && baseCount === 0) {
    return 0;
  }

  return baseCount + 1;
};

interface TableViewLayoutProps {
  filtersConfig: readonly any[];
  filters: any;
  pageName: string;
  columns: { key: string; label: string }[];
  pageSize: number;
  error?: unknown;
  onFilterChange: (key: string, value: any) => void;
  onResetFilters: () => void;
  onPageSizeChange: (limit: number) => void;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  table: any;
  showSidebar: boolean;
  setShowSidebar: (show: boolean) => void;
  toolbarButtons?: React.ReactNode;
  onRefresh?: () => void;
  topScrollBar?: boolean;
  isLoading?: boolean;
  isSilentRefresh?: boolean;
  isLoadingCount?: boolean;
  countUnavailable?: boolean;
  countErrorMessage?: string;
  rowClassName?: (row: any) => string | undefined;
  onRowClick?: (row: any) => void;
  /** See AdvanceTable: the row the side panel is showing. */
  selectedRowId?: string;
  /** Optional pane to the right of the table region (e.g. a details pane), resizable
      with a splitter. The table region is the same element whether or not this is
      present, so opening and closing it never remounts the table or loses its scroll
      position. */
  sidePanel?: React.ReactNode;
  /** Optional panel rendered as an in-flow flex sibling below the table region
      (e.g. the node-instance bottom panel), sharing the shell's height. */
  bottomPanel?: React.ReactNode;
}

const TableViewLayout = ({
  filtersConfig,
  filters,
  pageName,
  columns,
  pageSize,
  error,
  onFilterChange,
  onResetFilters,
  onPageSizeChange,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  table,
  showSidebar,
  setShowSidebar,
  toolbarButtons,
  onRefresh,
  topScrollBar,
  isLoading,
  isSilentRefresh = false,
  isLoadingCount = false,
  countUnavailable = false,
  countErrorMessage,
  rowClassName,
  onRowClick,
  selectedRowId,
  sidePanel,
  bottomPanel,
}: TableViewLayoutProps) => {
  const { t } = useTranslation();
  const [firstOpen, setFirstOpen] = useState(true);
  const [tableWidth, setTableWidth] = useState(TABLE_DEFAULT_WIDTH);
  const effectiveIsLoading = isLoading && !isSilentRefresh;
  const displayError = getErrorMessage(error, t('errors.unexpected'), null);

  const activeFilterCount = useMemo(() => countActiveFilters(filters, filtersConfig), [filters, filtersConfig]);

  return (
    <Container fluid className="p-0 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <OffCanvasSidebar
        showSide={showSidebar}
        onHide={() => setShowSidebar(false)}
        onShow={() => {
          // For time being it is hardcoded, but in future we might add option on the backend so that value on first filter open is configurable
          setFirstOpen(false);
          if (firstOpen) {
            onFilterChange('parentOnly', false);
          }
        }}
      >
        <FilterAndSearchRefactor
          filtersConfig={filtersConfig}
          filters={filters}
          pageName={pageName}
          pageSize={pageSize}
          onFilterChange={onFilterChange}
          onReset={onResetFilters}
          columns={columns}
          onPageSizeChange={onPageSizeChange}
        />
      </OffCanvasSidebar>

      <div className="d-flex justify-content-end flex-shrink-0">
        <ToggleUseLocalTimezone />
      </div>

      <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
        {/* Table region: the table body is the only scroll owner; the toolbar and
          pagination footer are fixed flex children so they stay visible. The same
          element with or without a side panel, so the table never remounts. */}
        <div
          className="d-flex flex-column"
          style={sidePanel ? { flex: '0 1 auto', width: tableWidth, minHeight: 0, minWidth: 0 } : { flex: '1 1 0', minHeight: 0, minWidth: 0 }}
        >
          {displayError && (
            <Alert variant="outline-danger" className="flex-shrink-0">
              {t('errors.somethingWentWrong')}: {displayError}
            </Alert>
          )}
          <div className="table-view-card">
            <div className="table-view-card__header">
              {toolbarButtons}
              <div className="d-flex gap-2 ms-auto">
                {onRefresh && (
                  <Button variant="outline-secondary" onClick={onRefresh} disabled={effectiveIsLoading} title={t('tableView.refresh')}>
                    <ArrowRepeat style={effectiveIsLoading ? { animation: 'spin 0.8s linear infinite' } : undefined} />
                  </Button>
                )}
                <Button className="position-relative" variant="secondary" onClick={() => setShowSidebar(true)}>
                  {t('tableView.searchAndFilter')}
                  {activeFilterCount > 0 && (
                    <Badge bg="primary" pill className="position-absolute top-0 start-100 translate-middle">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </div>
            </div>

            <AdvanceTableProvider {...table}>
              <AdvanceTable
                tableProps={{
                  size: 'sm',
                  hover: true,
                  className: 'phoenix-table fs-9 mb-0',
                }}
                rowClassName={rowClassName}
                onRowClick={onRowClick}
                selectedRowId={selectedRowId}
                topScrollBar={topScrollBar}
                isLoading={effectiveIsLoading}
              />
              <div className="table-view-card__footer">
                <AdvanceTableFooter
                  navBtn
                  pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={onPageChange}
                  isLoadingCount={isLoadingCount}
                  countUnavailable={countUnavailable}
                  countErrorMessage={countErrorMessage}
                />
              </div>
            </AdvanceTableProvider>
          </div>
        </div>

        {sidePanel && (
          <>
            <Splitter
              size={tableWidth}
              onResize={setTableWidth}
              min={TABLE_MIN_WIDTH}
              max={TABLE_MAX_WIDTH}
              side="before"
              className="mx-2"
              ariaLabel="Resize the table"
            />
            <div className="d-flex flex-column" style={{ flex: '1 1 0', minHeight: 0, minWidth: SIDE_PANEL_MIN_WIDTH }}>
              {sidePanel}
            </div>
          </>
        )}
      </div>

      {bottomPanel}
    </Container>
  );
};

export default TableViewLayout;
